import { useSeo } from '../lib/seo';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Gift, Calendar, Award, Mail } from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';
import api from '../lib/api';

const QUARTER_LABEL = (q) => {
  if (!q) return '';
  const [year, qn] = q.split('-Q');
  const names = { 1: 'Jan–Mar', 2: 'Apr–Jun', 3: 'Jul–Sep', 4: 'Oct–Dec' };
  return `${names[qn]} ${year}`;
};

export default function CompetitionPage() {
  useSeo(
    'Competitions & Giveaways | Go Leicestershire',
    'Enter our latest Leicestershire competitions and giveaways — days out, breaks and more to be won.'
  );
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get('/competition/current').then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-base" data-testid="competition-page">
      <Helmet>
        <title>Quarterly Prize Draw — Free StayPlayExplore Voucher · goLeicestershire</title>
        <meta name="description" content="Enter our free quarterly prize draw — every subscriber goes into the hat for a StayPlayExplore voucher. Four winners a year. UK residents, 18+. Full T&Cs inside." />
      </Helmet>
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="label text-heritage mb-3 inline-flex items-center gap-2">
          <Gift size={14}/> Quarterly prize draw
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
          Win a Leicestershire short break, on us.
        </h1>
        <p className="text-lg text-charcoal/80 mb-8 leading-relaxed">
          Every quarter — four times a year — we pick one subscriber from our newsletter list at random and send them a
          {' '}<Link to="/packages" className="text-heritage underline hover:text-heritage-deep">StayPlayExplore</Link> voucher.
          No purchase needed. Pick any current package — Family Classic, Heritage Weekend, you decide.
        </p>

        <div className="bg-white rounded-3xl p-7 lg:p-10 shadow-soft mb-10">
          <div className="label text-mustard mb-2">Enter — it&apos;s free</div>
          <h2 className="font-display text-2xl lg:text-3xl mb-4">Sign up and you&apos;re in.</h2>
          <NewsletterSignup variant="inline" source="competition-page" />
          {status && (
            <p className="text-sm text-charcoal/60 mt-4" data-testid="competition-status">
              <Calendar size={12} className="inline mr-1.5"/>
              Current draw: <strong className="text-charcoal">{QUARTER_LABEL(status.quarter)}</strong>{' '}
              · {status.entries} entries so far{status.last_draw ? ` · last winner: ${status.last_draw.winner_email}` : ''}
            </p>
          )}
        </div>

        <h2 className="font-display text-2xl lg:text-3xl mb-4 mt-12">How it works</h2>
        <div className="space-y-5 mb-12">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-mustard flex items-center justify-center flex-shrink-0"><Mail size={16}/></div>
            <div>
              <div className="font-semibold mb-1">1. Subscribe to the weekly digest</div>
              <p className="text-charcoal/70 text-sm">Tick the prize-draw box when you sign up. Already a subscriber? Re-submit any form with the box ticked to opt in.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-sky flex items-center justify-center flex-shrink-0"><Calendar size={16}/></div>
            <div>
              <div className="font-semibold mb-1">2. Each quarter, one winner is drawn</div>
              <p className="text-charcoal/70 text-sm">On the 1st of January, April, July and October we draw one entry at random from everyone who opted in during the previous three months.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-heritage text-base flex items-center justify-center flex-shrink-0"><Award size={16}/></div>
            <div>
              <div className="font-semibold mb-1">3. We email the winner directly</div>
              <p className="text-charcoal/70 text-sm">If you don&apos;t respond within 14 days we pick again. The winner chooses any current StayPlayExplore package — Family Classic, Heritage Weekend, anything that&apos;s live at the time.</p>
            </div>
          </div>
        </div>

        {/* Full T&Cs — required for UK free-entry prize-draw compliance */}
        <h2 className="font-display text-2xl lg:text-3xl mb-4 mt-12">Terms &amp; conditions</h2>
        <div className="prose prose-sm text-charcoal/80 max-w-none [&_h3]:font-semibold [&_h3]:text-charcoal [&_h3]:text-base [&_h3]:mt-6 [&_h3]:mb-2 [&_li]:my-1 [&_p]:my-3">
          <h3>1. The promoter</h3>
          <p>The Promoter is goLeicestershire (&quot;we&quot;, &quot;us&quot;). Email: <a className="text-heritage underline" href="mailto:hello@goleicestershire.com">hello@goleicestershire.com</a>.</p>

          <h3>2. No purchase necessary</h3>
          <p>Entry is completely free. You do not need to buy anything to enter or to win.</p>

          <h3>3. Eligibility</h3>
          <ul className="list-disc pl-6">
            <li>Open to UK residents aged 18 or over at the time of entry.</li>
            <li>Employees of the Promoter and their immediate families are not eligible.</li>
            <li>One entry per email address per quarterly draw.</li>
          </ul>

          <h3>4. How to enter</h3>
          <p>Subscribe to the goLeicestershire newsletter via any signup form on the site and tick the &quot;Enter me into the quarterly prize draw&quot; box. Alternatively, send a postcard with your name, email and postcode to <em>goLeicestershire Prize Draw, 2 High Street, Loughborough, LE11 2PY</em>. Postal entries are accepted up to 7 days before the draw date.</p>

          <h3>5. Draw dates</h3>
          <p>One draw per calendar quarter. Draws take place on or around the 1st of January, April, July and October each year, covering everyone who entered during the previous three months.</p>

          <h3>6. Selection of winner</h3>
          <p>Winners are selected at random from all valid opted-in entries received during the draw period. We use a random-number generator and the process is auditable internally.</p>

          <h3>7. The prize</h3>
          <p>One (1) StayPlayExplore voucher per quarter. The winner may choose any StayPlayExplore package that is current and bookable on the day they redeem the voucher. The voucher has no cash alternative and is not transferable. The voucher is valid for 12 months from the date of issue.</p>

          <h3>8. Notification</h3>
          <p>The winner will be contacted by the email address used to enter, within 7 days of the draw. If we don&apos;t receive a response within 14 days of notification, we reserve the right to redraw and pick another winner from the remaining entries.</p>

          <h3>9. Privacy</h3>
          <p>Your email is used only to notify you if you win and to send the weekly newsletter. We don&apos;t share your details with any third party for marketing. You can unsubscribe at any time with one click from any newsletter. See our <Link to="/privacy" className="text-heritage underline">privacy notice</Link> for the full picture.</p>

          <h3>10. Liability</h3>
          <p>The Promoter accepts no responsibility for entries that are not received for any reason. The Promoter&apos;s decision is final and no correspondence will be entered into.</p>

          <h3>11. Governing law</h3>
          <p>This promotion is governed by the laws of England and Wales.</p>
        </div>

        <p className="text-xs text-charcoal/50 mt-12">Last updated: February 2026.</p>
      </section>
    </div>
  );
}
